import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Smartphone } from 'lucide-react';
import { estimateRecipients, sendCampaign } from '../../../services/MasterAdmin/pushNotificationCampaignService';
import type { PushNotificationCampaign } from '../../../models/MasterAdmin/pushNotificationCampaign';

type PlatformChoice = 'android' | 'ios' | 'web' | 'both';

function storedPlatformToUi(stored: string | undefined): PlatformChoice {
  const u = (stored || 'BOTH').toUpperCase();
  if (u === 'ANDROID') return 'android';
  if (u === 'IOS') return 'ios';
  if (u === 'WEB') return 'web';
  return 'both';
}

/** Query string for GET /estimate — must match send payload semantics (both → android + ios). */
function platformChoiceToQuery(p: PlatformChoice): string {
  if (p === 'both') return 'android,ios';
  return p;
}

type Props = {
  open: boolean;
  /** Always defined when the modal is mounted (parent gates on sendTarget). */
  campaign: PushNotificationCampaign;
  onClose: () => void;
  onSent: () => void;
};

const SendConfirmModal: React.FC<Props> = ({ open, campaign, onClose, onSent }) => {
  const [platforms, setPlatforms] = useState<PlatformChoice>(() => storedPlatformToUi(campaign.targetPlatform));
  const [count, setCount] = useState<number | null>(null);
  const [baseCount, setBaseCount] = useState<number | null>(null);
  const [afterConditions, setAfterConditions] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSendAgain = campaign.status === 'SENT';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    estimateRecipients(campaign.id, { platforms: platformChoiceToQuery(platforms) })
      .then((r) => {
        if (cancelled) return;
        setCount(r.data.reachableFcmCount ?? r.data.estimatedRecipients);
        setBaseCount(r.data.baseAudienceCount ?? null);
        setAfterConditions(r.data.afterConditionsCount ?? null);
        setNote(r.data.note || null);
      })
      .catch(() => {
        if (!cancelled) setCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, campaign.id, platforms]);

  if (!open) return null;

  const togglePlatform = (key: PlatformChoice) => {
    setPlatforms(key);
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const payload =
        platforms === 'both'
          ? { platforms: ['both'] as ('android' | 'ios' | 'web' | 'both')[] }
          : { platforms: [platforms] };
      await sendCampaign(campaign.id, payload);
      onSent();
      onClose();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: { message?: string } } }).response?.data?.message : null;
      setError(msg || 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative z-10 flex min-h-0 max-h-[min(90vh,760px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isSendAgain ? 'Send notification again' : 'Send notification'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-4">
          <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-600 dark:bg-gray-900/40">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Preview</p>
            {campaign.imageUrl && (
              <img
                src={campaign.imageUrl}
                alt=""
                className="mt-2 max-h-36 w-full rounded-lg object-contain object-center"
              />
            )}
            <p className="mt-2 font-semibold text-gray-900 dark:text-white">{campaign.title}</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{campaign.message}</p>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-900/30">
            <Smartphone className="h-5 w-5 text-gray-500" />
            <div className="w-full">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Audience preview</p>
              {loading ? (
                <p className="text-lg font-semibold text-blue-600">…</p>
              ) : (
                <div className="mt-1 space-y-1 text-sm">
                  {baseCount != null && (
                    <p className="text-gray-700 dark:text-gray-300">Base audience: <span className="font-semibold">{baseCount.toLocaleString()}</span></p>
                  )}
                  {afterConditions != null && afterConditions !== baseCount && (
                    <p className="text-gray-700 dark:text-gray-300">After conditions: <span className="font-semibold">{afterConditions.toLocaleString()}</span></p>
                  )}
                  <p className="text-gray-700 dark:text-gray-300">
                    Reachable FCM recipients:{' '}
                    <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {count != null ? count.toLocaleString() : '—'}
                    </span>
                  </p>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {note || 'Reachable FCM recipients are devices with a valid token. This is not always the same as the user count.'}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">Platform</p>
            <div className="flex flex-wrap gap-2">
              {(['android', 'ios', 'web', 'both'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePlatform(key)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium ring-1 transition ${
                    platforms === key
                      ? 'bg-blue-600 text-white ring-blue-600'
                      : 'bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600'
                  }`}
                >
                  {key === 'both'
                    ? 'Android & iOS'
                    : key === 'ios'
                      ? 'iOS'
                      : key === 'web'
                        ? 'Web'
                        : 'Android'}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {sending ? 'Sending…' : isSendAgain ? 'Send again' : 'Send now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendConfirmModal;
